import type { EstimatorBand, EstimatorSeparation } from '../api'

/**
 * The ballot, as intervals — and the one comparison that resolves it.
 *
 * ## Why the interval and the gap are drawn separately
 *
 * A reader given a chart with error bars performs one test on it: do the bars touch? That
 * test is the wrong one for two shares of the same question. They come from one multinomial
 * and are negatively correlated, so the margin on their DIFFERENCE is narrower than the
 * overlap of their two intervals implies — the server computes it as
 * `[p₁ + p₂ − (p₁ − p₂)²] / n_eff`, and the two answers disagree far more often than
 * anybody expects.
 *
 * So the top half draws the intervals, because that is what the poll measured, and the
 * strip beneath draws the gap against its own margin, because that is what actually
 * settles the lead. Drawing only the first would invite the wrong reading; drawing only
 * the second would hide the shares. The disagreement between them, when it happens, is
 * called out in words — it is the most valuable sentence on the page.
 *
 * Nothing here computes a percentage. Every value is served.
 */
export function LeadPlot({
  bands, separation,
}: {
  bands: EstimatorBand[]
  separation: EstimatorSeparation | null
}) {
  const shown = bands.filter((b) => !b.suppressed && b.estimate != null)
  if (shown.length === 0) {
    return (
      <p className="text-erp-sm text-muted-foreground">
        Every option on this question is withheld, so there is no distribution to draw.
        {bands[0]?.suppressedSentence ? ` ${bands[0].suppressedSentence}` : ''}
      </p>
    )
  }

  // The axis is padded past the widest interval rather than fitted to it, so a bar that
  // reaches the edge is a real reading and not the frame clipping it.
  const hi = Math.min(1, Math.max(...shown.map((b) => b.ciUpper ?? b.estimate ?? 0)) * 1.15)
  const x = (v: number) => `${Math.max(0, Math.min(100, (v / hi) * 100))}%`
  const pct = (v: number | null | undefined, d = 1) =>
    v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const pp = (v: number | null | undefined, d = 1) =>
    v == null ? '—' : `±${(v * 100).toFixed(d)} pts`

  const disagrees = separation?.separated === true && separation.intervalsOverlap === true

  return (
    <div className="space-y-4">
      <ul className="space-y-2.5">
        {shown.map((b) => (
          <li key={b.optionId}
              className="grid grid-cols-[minmax(110px,24%)_1fr_auto] items-center gap-3">
            <span className="truncate text-erp-sm" title={b.optionLabel}>{b.optionLabel}</span>
            <span className="relative h-5">
              {/* The 95% interval is the light band; the point estimate is the tick in it.
                  A bar alone would say the estimate is the finding, which it is not. */}
              <span className="absolute inset-y-1.5 rounded-full bg-muted"
                    style={{ left: 0, right: 0 }} />
              <span className="absolute inset-y-1 rounded-full bg-chocolate/25"
                    style={{
                      left: x(b.ciLower ?? b.estimate ?? 0),
                      width: `calc(${x((b.ciUpper ?? 0) - (b.ciLower ?? 0))})`,
                    }} />
              <span className="absolute inset-y-0 w-0.5 bg-chocolate"
                    style={{ left: x(b.estimate ?? 0) }}
                    title={`${b.optionLabel}: ${pct(b.estimate)} ${pp(b.moe95)}`} />
            </span>
            <span className="tabular-nums text-erp-sm whitespace-nowrap">
              {pct(b.estimate)}
              <span className="ml-1.5 text-erp-xs text-muted-foreground">{pp(b.moe95)}</span>
            </span>
          </li>
        ))}
      </ul>

      {separation && (
        <div className="rounded-md border border-border p-3 space-y-1.5">
          <p className="text-erp-xs uppercase tracking-wide text-muted-foreground">
            The comparison that settles it
          </p>
          <p className="text-erp-sm">
            <span className="font-medium">{separation.aLabel}</span> over{' '}
            <span className="font-medium">{separation.bLabel}</span>:{' '}
            <span className="tabular-nums font-medium">{pct(separation.gap)}</span>{' '}
            <span className="tabular-nums text-muted-foreground">{pp(separation.gapMoe95)}</span>
            {separation.leadProbability != null && (
              <> · lead is genuine with probability{' '}
                <span className="tabular-nums">{pct(separation.leadProbability, 0)}</span></>
            )}
          </p>
          <p className={`text-erp-sm ${separation.separated ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
            {separation.separated
              ? 'Separated — the gap clears its own margin.'
              : 'Not separated — the gap does not clear its own margin.'}
          </p>
          {disagrees && (
            <p className="text-erp-xs text-muted-foreground">
              Note that the two options' own intervals overlap above. That is the test most
              readers apply to a chart with error bars, and it is the wrong one for two shares
              of the same question — it ignores that the shares are negatively correlated and
              so overstates the uncertainty. The gap, with its own margin, is separated.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
